// =============================================================================
// features/sales/campaigns/operate/components/ReturnMemberDialog.tsx —
// Devolução JUSTIFICADA do item reservado (doc 26 §6.8 "só habilita depois
// de registrar o resultado OU devolver justificadamente o item").
//
// Mesmo padrão de erro-preserva-formulário de
// features/sales/contacts/RegisterContactDialog.tsx: um erro de rede NUNCA
// limpa o motivo digitado — o vendedor só perde o texto se cancelar de
// propósito (briefing do slot #2).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { cn } from '../../../../../lib/cn';
import { FormTextarea } from '../../../components/FormTextarea';
import { useFocusTrap } from '../../../components/useFocusTrap';

export interface ReturnMemberDialogProps {
  leadName: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<{ ok: true } | { ok: false; message: string }>;
}

export function ReturnMemberDialog({
  leadName,
  onClose,
  onConfirm,
}: ReturnMemberDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [isPending, setIsPending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const validationError =
    touched && reason.trim().length === 0
      ? 'Explique por que está devolvendo este item.'
      : undefined;

  const handleEscape = React.useCallback(() => {
    if (!isPending) onClose();
  }, [isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setTouched(true);
    if (reason.trim().length === 0) return;

    setIsPending(true);
    setSubmitError(null);
    const result = await onConfirm(reason.trim());
    setIsPending(false);
    if (!result.ok) {
      setSubmitError(result.message);
      return;
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="return-member-title"
        className={cn('w-full max-w-md rounded-md flex flex-col overflow-hidden')}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex flex-col gap-1 px-6 py-5"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2
            id="return-member-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Devolver cliente à fila
          </h2>
          <p className="font-sans text-sm text-ink-3">{leadName}</p>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          noValidate
          className="flex flex-col gap-4 px-6 py-5"
        >
          <p className="font-sans text-sm text-ink-2 leading-relaxed">
            Este cliente volta para a fila e pode ser reservado por qualquer vendedor, sem que você
            registre um resultado agora. Explique o motivo — fica registrado na auditoria da
            campanha.
          </p>

          <FormTextarea
            id="return-member-reason"
            label="Motivo da devolução"
            required
            placeholder="Ex.: cliente pediu para ligar em outro horário, telefone ocupado, etc."
            disabled={isPending}
            error={validationError}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">Erro ao devolver</p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
              <p className="font-sans text-xs text-ink-3 mt-1">
                O motivo que você escreveu continua aqui — pode tentar novamente.
              </p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="submit"
              variant="danger"
              disabled={isPending}
              className="w-full justify-center"
            >
              {isPending ? 'Devolvendo…' : submitError ? 'Tentar novamente' : 'Confirmar devolução'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
